import { useMemo, useState } from "react";
import { AlertCircle, Bot, Loader2 } from "lucide-react";
import { toast } from "sonner";

import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  useMarkServiceAccounts,
  useTenantEmployees,
} from "@/hooks/use-service-accounts";
import {
  DEFAULT_THRESHOLD,
  confidenceLevel,
  detectServiceAccounts,
  type ConfidenceLevel,
} from "@/lib/solution-engineer/service-accounts";
import { ToolCard } from "./tool-card";

const CONFIDENCE_BADGES: Record<
  ConfidenceLevel,
  { label: string; className: string }
> = {
  high: { label: "High", className: "bg-red-500/15 text-red-700 dark:text-red-400" },
  "medium-high": {
    label: "Med-high",
    className: "bg-orange-500/15 text-orange-700 dark:text-orange-400",
  },
  medium: {
    label: "Medium",
    className: "bg-amber-500/15 text-amber-700 dark:text-amber-400",
  },
  low: { label: "Low", className: "bg-muted text-muted-foreground" },
};

export function ServiceAccountsTool({ tenantId }: { tenantId: string }) {
  const [scanned, setScanned] = useState(false);
  const [threshold, setThreshold] = useState(DEFAULT_THRESHOLD);
  const [selected, setSelected] = useState<Set<string>>(new Set());
  const [confirming, setConfirming] = useState(false);

  return (
    <ToolCard
      icon={Bot}
      title="Identify service accounts"
      description="Scores every employee with the classify-service-accounts heuristics (email, name and missing HR attributes) and marks the ones you approve as service accounts."
    >
      {!scanned ? (
        <Button size="sm" onClick={() => { setScanned(true); }}>
          Scan employees
        </Button>
      ) : (
        <ServiceAccountsResults
          tenantId={tenantId}
          threshold={threshold}
          onThresholdChange={(value) => {
            setThreshold(value);
            setSelected(new Set());
          }}
          selected={selected}
          onSelectedChange={setSelected}
          confirming={confirming}
          onConfirmingChange={setConfirming}
        />
      )}
    </ToolCard>
  );
}

interface ServiceAccountsResultsProps {
  tenantId: string;
  threshold: number;
  onThresholdChange: (value: number) => void;
  selected: Set<string>;
  onSelectedChange: (selected: Set<string>) => void;
  confirming: boolean;
  onConfirmingChange: (open: boolean) => void;
}

function ServiceAccountsResults({
  tenantId,
  threshold,
  onThresholdChange,
  selected,
  onSelectedChange,
  confirming,
  onConfirmingChange,
}: ServiceAccountsResultsProps) {
  const { data: employees, isLoading, isError, refetch } = useTenantEmployees(tenantId);
  const mark = useMarkServiceAccounts(tenantId);

  const candidates = useMemo(
    () => detectServiceAccounts(employees ?? [], threshold),
    [employees, threshold],
  );
  const alreadyMarked = useMemo(
    () => (employees ?? []).filter((e) => e.type === "service_account").length,
    [employees],
  );

  if (isLoading) {
    return (
      <div className="text-muted-foreground flex items-center gap-2 text-sm">
        <Loader2 className="h-4 w-4 animate-spin" />
        Loading employees...
      </div>
    );
  }

  if (isError || !employees) {
    return (
      <div className="flex items-center gap-3 text-sm">
        <AlertCircle className="text-destructive h-4 w-4" />
        Failed to load employees.
        <Button variant="outline" size="sm" onClick={() => void refetch()}>
          Retry
        </Button>
      </div>
    );
  }

  const allSelected =
    candidates.length > 0 && candidates.every((c) => selected.has(c.employee.id));

  const toggle = (id: string, checked: boolean) => {
    const next = new Set(selected);
    if (checked) next.add(id);
    else next.delete(id);
    onSelectedChange(next);
  };

  const onConfirm = () => {
    mark.mutate([...selected], {
      onSuccess: ({ updated, failed }) => {
        onSelectedChange(new Set());
        if (failed.length === 0) {
          toast.success(`Marked ${updated.length} as service accounts`);
        } else {
          toast.error(
            `Marked ${updated.length}, ${failed.length} failed: ${failed
              .map((f) => f.error)
              .slice(0, 3)
              .join("; ")}`,
          );
        }
      },
      onError: () => {
        toast.error("Failed to mark service accounts");
      },
      onSettled: () => {
        onConfirmingChange(false);
      },
    });
  };

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-end justify-between gap-4">
        <div className="space-y-1.5">
          <Label htmlFor="sa-threshold">Threshold</Label>
          <Input
            id="sa-threshold"
            type="number"
            min={0}
            step={5}
            value={threshold}
            onChange={(e) => {
              onThresholdChange(Number(e.target.value) || 0);
            }}
            className="w-24"
          />
        </div>
        <p className="text-muted-foreground text-sm">
          {employees.length} employees · {alreadyMarked} already service
          accounts · {candidates.length} suggested
        </p>
        <Button
          size="sm"
          disabled={selected.size === 0 || mark.isPending}
          onClick={() => { onConfirmingChange(true); }}
        >
          Mark {selected.size || ""} as service accounts
        </Button>
      </div>

      {candidates.length === 0 ? (
        <p className="text-muted-foreground text-sm">
          No employees score {threshold} or more. Lower the threshold to see
          weaker matches.
        </p>
      ) : (
        <div className="max-h-[420px] overflow-auto rounded-md border">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead className="w-10">
                  <Checkbox
                    aria-label="Select all"
                    checked={allSelected}
                    onCheckedChange={(state) => {
                      onSelectedChange(
                        state === true
                          ? new Set(candidates.map((c) => c.employee.id))
                          : new Set(),
                      );
                    }}
                  />
                </TableHead>
                <TableHead>Employee</TableHead>
                <TableHead>Score</TableHead>
                <TableHead>Evidence</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {candidates.map(({ employee, score, reasons }) => {
                const badge = CONFIDENCE_BADGES[confidenceLevel(score)];
                return (
                  <TableRow key={employee.id}>
                    <TableCell>
                      <Checkbox
                        aria-label={`Select ${employee.work_email}`}
                        checked={selected.has(employee.id)}
                        onCheckedChange={(state) => {
                          toggle(employee.id, state === true);
                        }}
                      />
                    </TableCell>
                    <TableCell className="max-w-[260px]">
                      <div className="truncate text-sm font-medium">
                        {employee.display_name || "—"}
                      </div>
                      <div className="text-muted-foreground truncate text-xs">
                        {employee.work_email}
                      </div>
                    </TableCell>
                    <TableCell>
                      <Badge variant="secondary" className={badge.className}>
                        {score} · {badge.label}
                      </Badge>
                    </TableCell>
                    <TableCell className="text-muted-foreground text-xs whitespace-normal">
                      {reasons.join(", ")}
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </div>
      )}

      <AlertDialog open={confirming} onOpenChange={onConfirmingChange}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>
              Mark {selected.size} employees as service accounts?
            </AlertDialogTitle>
            <AlertDialogDescription>
              Their employee type changes to service account in the
              customer&apos;s tenant, which drops them from employee search.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={mark.isPending}>Cancel</AlertDialogCancel>
            <AlertDialogAction
              disabled={mark.isPending}
              onClick={(e) => {
                e.preventDefault();
                onConfirm();
              }}
            >
              {mark.isPending && <Loader2 className="mr-1 h-4 w-4 animate-spin" />}
              Mark as service accounts
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
